import { AfterViewInit, Component, ElementRef, NgZone, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { createGesture, Gesture, GestureDetail } from '@ionic/core';

import { Subscription } from 'rxjs';

import { PlacePanel } from '@unpispas/upp-base';
import { alertService } from '@unpispas/upp-base';
import { geocodeService } from '@unpispas/upp-base';
import { languageService, languageSupport } from '@unpispas/upp-base';
import { toastService } from '@unpispas/upp-base';
import { ViewRenderer } from '@unpispas/upp-wdgt';
import { viewService } from '@unpispas/upp-base';

import { Product } from '@unpispas/upp-data';
import { Preselect } from '@unpispas/upp-data';

import { PlaceView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';
import { TicketDiscountView } from '@unpispas/upp-data';
import { TicketExtraView } from '@unpispas/upp-data';
import { TicketOfferView } from '@unpispas/upp-data';
import { TicketProductView } from '@unpispas/upp-data';
import { ProductView } from '@unpispas/upp-data';
import { PreselectView } from '@unpispas/upp-data';
import { ProductOptView } from '@unpispas/upp-data';

/********************************/
/* Products search              */
/********************************/

type OrderSelectable = ProductView | PreselectView;

class ProductsSearch {
    private _delay_timeout: ReturnType<typeof setTimeout> | null = null;
    private _search_result: Array<OrderSelectable> = [];

    constructor(private place: PlaceView | null, private change: ViewRenderer) {
        // nothing to do
    }

    private get MinLength(): number {
        return 3;
    }

    private _criteria = '';
    get criteria(): string {
        return this._criteria;
    }

    set criteria(value: string) {
        this._criteria = value;

        if (this._delay_timeout) {
            clearTimeout(this._delay_timeout);
            this._delay_timeout = null;
        }

        if (this._criteria.length > this.MinLength) {
            this._delay_timeout = setTimeout(() => {
                this.doSearch(this._criteria);
            }, 500);
        }
        else {
            this._search_result = [];
            this.change.markForCheck();
        }
    }

    get results(): Array<OrderSelectable> {
        return this._search_result;
    }

    get Searching(): boolean {
        return (!!this._criteria) && (this.criteria.length > this.MinLength);
    }

    Clear(): void {
        this._criteria = '';
        this._search_result = [];
        if (this._delay_timeout) {
            clearTimeout(this._delay_timeout);
            this._delay_timeout = null;
        }
        this.change.markForCheck();
    }

    private doSearch(criteria: string): void {
        if (!this.place) {
            return;
        }

        this._search_result = [];

        const _results = this.place.doSearch(criteria);
        for (const _result of _results) {
            if (_result.ViewOf(Product) || _result.ViewOf(Preselect)) {
                this._search_result.push(_result as OrderSelectable);
            }
        }

        this.change.markForCheck();
    }
}

/********************************/
/* Guest order close reason     */
/********************************/

export type OrderCloseReason = 'sent' | 'cancelled' | 'back';

/** Guest catalog + cart panel (home panel `ORDER`). */
@Component({
    selector: 'upp-guest-order',
    templateUrl: './upp-order.html',
    styleUrls: ['./upp-order.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef],
    }],
})
export class UppGuestOrderComponent extends languageSupport implements OnChanges, AfterViewInit, OnDestroy {
    @Input() place: PlaceView | null = null;
    @Input() ticket: TicketView | null = null;

    @Output() Close = new EventEmitter<OrderCloseReason>();

    @ViewChild('swipeHost', { read: ElementRef }) swipeHost?: ElementRef<HTMLElement>;

    /********************************/
    /* Subscriptions                */
    /********************************/

    private _place_subscription: Subscription | null = null;
    private _ticket_subscription: Subscription | null = null;
    private _cart_swipe_gesture: Gesture | null = null;
    private _cart_swipe_timeout: ReturnType<typeof setTimeout> | null = null;
    private _cart_loaded = false;

    constructor(
        private lang: languageService,
        private change: ViewRenderer,
        private alertCtrl: alertService,
        private geocode: geocodeService,
        private toast: toastService,
        public view: viewService,
        private zone: NgZone,
    ) {
        super(lang, change.cdref);
    }

    ngOnChanges(): void {
        this._productsearch = null;

        if (this._place_subscription) {
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        if (this._ticket_subscription) {
            this._ticket_subscription.unsubscribe();
            this._ticket_subscription = null;
        }

        if (this.place) {
            this._place_subscription = this.place.OnViewRefresh.subscribe(
            () => {
                this.change.markForCheck();
            });
        }

        if (this.ticket) {
            this._ticket_subscription = this.ticket.OnRefresh.subscribe(
            () => {
                this.change.markForCheck();
            });
        }
        else {
            this._showcart = false;
        }

        this.SliderSetup();
        this.change.markForCheck();
    }

    ngAfterViewInit(): void {
        this.SliderSetup();
    }

    ngOnDestroy(): void {
        if (this._cart_swipe_timeout != null) {
            clearTimeout(this._cart_swipe_timeout);
            this._cart_swipe_timeout = null;
        }

        if (this._cart_swipe_gesture) {
            this._cart_swipe_gesture.destroy();
            this._cart_swipe_gesture = null;
        }

        if (this._place_subscription) {
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        if (this._ticket_subscription) {
            this._ticket_subscription.unsubscribe();
            this._ticket_subscription = null;
        }

        super.OnDestroy();
    }

    /********************************/
    /* Catalog / search             */
    /********************************/

    private _productsearch: ProductsSearch | null = null;
    get search(): ProductsSearch {
        if (!this._productsearch) {
            this._productsearch = new ProductsSearch(this.place, this.change);
        }
        return this._productsearch;
    }

    get HasCatalogProducts(): boolean {
        return (this.place?.products.length ?? 0) > 0;
    }

    OnDismiss(): void {
        this.Close.emit('back');
    }

    /********************************/
    /* Product configurator         */
    /********************************/

    private _configuring: ProductView | null = null;
    get ConfiguringProduct(): ProductView | null {
        return this._configuring;
    }

    private _configuringOptions: Array<ProductOptView> = [];
    get ConfiguringOptions(): Array<ProductOptView> {
        return this._configuringOptions;
    }

    private _configuringCartAmount = 0;
    /** Units of {@link ConfiguringProduct} already in the guest ticket cart. */
    get ConfiguringCartAmount(): number {
        return this._configuringCartAmount;
    }

    /**
     * Drafting is always allowed when a place is set. Closed hours and
     * AcceptQR only gate submit via {@link CanSubmitOrder}.
     */
    CanAdd(_product: ProductView): boolean {
        return !!this.place;
    }

    OnSelect(item: ProductView | PreselectView | null): void {
        if (!item || !item.IsAvailable || !this.ticket) {
            return;
        }

        if (item.ViewOf(Product)) {
            const leaf = item as ProductView;
            if (leaf.IsFamily || leaf.IsGroup) {
                return;
            }

            if (!this.CanAdd(leaf)) {
                return;
            }

            this._OpenConfigurator(leaf, this._CartOptionsOf(leaf));
            return;
        }

        if (!item.ViewOf(Preselect)) {
            return;
        }

        const select = item as PreselectView;
        const parent = select.product;
        if (!parent || !this.CanAdd(parent)) {
            return;
        }

        this._OpenConfigurator(parent, (select.options ?? []).slice(), this._DirectInCart(select));
    }

    OnConfiguratorClosed(): void {
        this._configuring = null;
        this._configuringOptions = [];
        this._configuringCartAmount = 0;
        this.SliderSetup();
        this.change.markForCheck();
    }

    private _CartOptionsOf(product: ProductView): Array<ProductOptView> {
        if (!this.ticket) {
            return [];
        }

        for (const line of this.ticket.products) {
            if (line.InCart && line.product === product) {
                return line.options?.slice() ?? [];
            }
        }

        return [];
    }

    /** Units already in cart for lines matching this preselect shortcut. */
    private _DirectInCart(select: PreselectView): number {
        if (!this.ticket) {
            return 0;
        }

        let amount = 0;
        for (const line of this.ticket.products) {
            if (line.InCart && line.IsDirect(select)) {
                amount += line.amount || 0;
            }
        }

        return amount;
    }

    private _OpenConfigurator(product: ProductView, options: Array<ProductOptView>, incart?: number): void {
        this._configuringCartAmount = incart ?? (this.ticket?.AmountOfProduct(product) ?? 0);
        this._configuringOptions = options;
        this._configuring = product;
        
        this.change.markForCheck();
    }

    /********************************/
    /* Cart panel                   */
    /********************************/

    private _showcart = false;
    get ShowCart(): boolean {
        return this._showcart;
    }

    set ShowCart(value: boolean) {
        if (this._showcart === value) {
            return;
        }
        this._showcart = value;
        this.SliderSetup();
        this.change.markForCheck();
    }

    /**
     * Sticky mount for the cart layer: set on first visit; clear when the ticket leaves.
     */
    get CartLoaded(): boolean {
        return this._cart_loaded;
    }

    OnShowCart(): void {
        this.ShowCart = true;
    }

    OnHideCart(): void {
        this.ShowCart = false;
    }

    /********************************/
    /* Mobile swipe gesture         */
    /********************************/

    /**
     * Ionic-style horizontal swipe between catalog and cart.
     * Same `createGesture` host pattern as POS `upp-place-cart`.
     * Enabled in both view modes: QRA has no desktop split panel, only ShowCart.
     */
    private SliderSetup(): void {
        this._SetCartLoaded();

        if (this._cart_swipe_timeout != null) {
            clearTimeout(this._cart_swipe_timeout);
            this._cart_swipe_timeout = null;
        }

        this._cart_swipe_timeout = setTimeout(() => {
            this._cart_swipe_timeout = null;

            if (this._cart_swipe_gesture) {
                this._cart_swipe_gesture.destroy();
                this._cart_swipe_gesture = null;
            }

            const el = this.swipeHost?.nativeElement;
            if (!el || !this.ticket || !!this.ConfiguringProduct) {
                return;
            }

            const swipeCommitMinPx = 36;
            const swipeVelocityTrigger = 0.28;

            this._cart_swipe_gesture = createGesture({
                el,
                gestureName: 'guestorder-select-cart-swipe',
                gesturePriority: 20,
                threshold: 15,
                direction: 'x',
                maxAngle: 38,
                canStart: () => this.CanSwipeStart(),
                onEnd: (detail: GestureDetail) => {
                    const { deltaX, velocityX } = detail;
                    let direction: 'next' | 'prev' | null = null;

                    if (deltaX <= -swipeCommitMinPx || velocityX <= -swipeVelocityTrigger) {
                        direction = 'next';
                    }
                    else if (deltaX >= swipeCommitMinPx || velocityX >= swipeVelocityTrigger) {
                        direction = 'prev';
                    }

                    if (direction === null) {
                        return;
                    }

                    this.zone.run(() => {
                        this.OnSlide(direction);
                    });
                },
            });

            this._cart_swipe_gesture.enable(true);
        }, 0);

        this.change.markForCheck();
    }

    private _SetCartLoaded(): void {
        if (!this.ticket) {
            this._cart_loaded = false;
            return;
        }

        if (this.ShowCart) {
            this._cart_loaded = true;
        }
    }

    /** Swipe is off while the configurator is open or without a ticket. */
    CanSwipeStart(): boolean {
        if (!!this.ConfiguringProduct || !this.ticket) {
            return false;
        }
        return true;
    }

    /**
     * Cycles catalog (0) ↔ cart (1). Next = left swipe, prev = right swipe.
     */
    OnSlide(direction: 'next' | 'prev'): void {
        if (!this.ticket || !!this.ConfiguringProduct) {
            return;
        }

        const idx = this.ShowCart ? 1 : 0;
        const nextIndex = direction === 'next' ? (idx + 1) % 2 : (idx - 1 + 2) % 2;
        this.ShowCart = nextIndex === 1;
    }

    TicketProductTrack(_index: number, line: TicketProductView): any {
        return line?._uuid || null;
    }

    ExtraTrack(_index: number, extra: TicketExtraView): any {
        return extra?._uuid || null;
    }

    OfferTrack(_index: number, offer: TicketOfferView): any {
        return offer?._uuid || null;
    }

    DiscountTrack(_index: number, discount: TicketDiscountView): any {
        return discount?._uuid || null;
    }

    OnCartLineConfigure(panel: PlacePanel): void {
        const _product = panel.args?.['product'] as ProductView | null | undefined;
        if (!_product) {
            return;
        }

        const _options = (panel.args?.['options'] as Array<ProductOptView>) || [];
        this._OpenConfigurator(_product, _options);
    }

    OnCartUpdated(): void {
        this.change.markForCheck();
    }

    get Comments(): string | null {
        return this.ticket?.comments ?? null;
    }

    set Comments(value: string | null) {
        if (!this.ticket) {
            return;
        }

        this.ticket.comments = (value === '' || value == null) ? null : value;
        this.change.markForCheck();
    }

    /********************************/
    /* Venue hours / QR send gate   */
    /********************************/

    get OrderStatus(): 'qr' | 'all' | 'bar' | 'kit' | null {
        const _place = this.place;
        if (!_place) {
            return null;
        }

        if (!_place.AcceptQR) {
            return 'qr';
        }

        const _barClosed = !_place.IsBarOpen;
        const _kitClosed = !_place.IsKitOpen;

        if (this.ShowCart && this.ticket && !this.ticket.IsEmpty) {
            const _barBlocks = _barClosed && this._HasCartType('BAR');
            const _kitBlocks = _kitClosed && this._HasCartType('KIT');
            if (_barBlocks && _kitBlocks) {
                return 'all';
            }
            if (_barBlocks) {
                return 'bar';
            }
            if (_kitBlocks) {
                return 'kit';
            }
            return null;
        }

        if (_barClosed && _kitClosed) {
            return 'all';
        }
        if (_barClosed) {
            return 'bar';
        }
        if (_kitClosed) {
            return 'kit';
        }
        return null;
    }

    /** Same copy for every closed/QR-off reason — no bar/kitchen detail. */
    get OrderStatusTitle(): string {
        return this.OrderStatus ? '@place_all_closed_title' : '';
    }

    get OrderStatusMessage(): string {
        return this.OrderStatus ? '@place_all_closed_message' : '';
    }

    /**
     * Venue / QR blocks send (drafting still allowed). Distinct from empty-cart
     * `!CanSend` so the send button can stay clickable and show an info modal.
     */
    get IsOrdersBlocked(): boolean {
        if (!this.place) {
            return false;
        }

        if (!this.place.AcceptQR) {
            return true;
        }

        if (this._HasCartType('BAR') && !this.place.IsBarOpen) {
            return true;
        }

        if (this._HasCartType('KIT') && !this.place.IsKitOpen) {
            return true;
        }

        return false;
    }

    get CanSubmitOrder(): boolean {
        return !!this.place && !!this.ticket?.CanSend && !this.IsOrdersBlocked;
    }

    private _HasCartType(type: 'BAR' | 'KIT'): boolean {
        if (!this.ticket) {
            return false;
        }

        for (const line of this.ticket.products) {
            if (line.type === type) {
                return true;
            }
        }
        return false;
    }

    /********************************/
    /* Send / cancel                */
    /********************************/

    private _sending = false;
    get IsSending(): boolean {
        return this._sending;
    }

    set IsSending(value: boolean) {
        this._sending = value;
        this.change.markForCheck();
    }

    async OnSendTicket(event: Event): Promise<void> {
        event.stopPropagation();
        if (!this.ticket || !this.place || this.IsSending) {
            return;
        }

        if (this.IsOrdersBlocked) {
            await this._ShowOrdersBlocked();
            return;
        }

        if (!this.CanSubmitOrder) {
            return;
        }

        this.IsSending = true;
        const _checkGeo = this.place.QrGeolocation !== false;
        const _neartable = !_checkGeo || await this.geocode.NearBy(this.place.Coordinates);
        if (_neartable) {
            if (this.ticket.comments != null) {
                this.ticket.comments = String(this.ticket.comments).trim() || null;
            }

            this.ticket.status = this.ticket.IsEmpty
                ? 'DE'
                : (this.ticket.IsPrepay ? 'PR' : 'AC');

            void this.toast.ShowWait();
            try {
                await this.ticket.OnCommit(null);
                // Guest provisional numbers: server may assign `invceno` (T…) while
                // `series` stays empty on the echoed row — still a successful send.
                if (!this.ticket.IsEmpty && !this.ticket.invceno) {
                    this.toast.ShowAlert(
                        'danger',
                        this.lang.tr('@service_request_error_4'),
                        3000,
                    );
                }
                else {
                    this.Close.emit('sent');
                }
            }
            finally {
                this.toast.HideWait();
            }
        }

        this.IsSending = false;
        if (_checkGeo) {
            this.geocode.NearBy(null);
        }
        this.change.markForCheck();
    }

    async OnCancel(event?: Event): Promise<void> {
        event?.stopPropagation();
        if (!this.ticket) {
            return;
        }

        if (!this.ticket.IsEmpty) {
            const confirmed = await this._Confirm(
                '@ticket_cancel_confirm_tittle',
                '@ticket_cancel_confirm_message',
            );
            if (!confirmed) {
                return;
            }
        }

        await this._CancelTicket();
    }

    async OnClear(event?: Event): Promise<void> {
        event?.stopPropagation();
        if (!this.ticket) {
            return;
        }

        if (!this.ticket.IsEmpty) {
            const confirmed = await this._Confirm(
                '@ticket_remove_all_confirm_tittle',
                '@ticket_remove_all_confirm_message',
            );
            if (!confirmed) {
                return;
            }
        }

        await this._CancelTicket();
    }

    private async _CancelTicket(): Promise<void> {
        if (!this.ticket) {
            return;
        }

        if (!this.ticket.ToInsert) {
            this.ticket.OnCancel();
            await this.ticket.OnCommit(null);
        }

        this.Close.emit('cancelled');
        this.change.markForCheck();
    }

    private _Confirm(headerKey: string, messageKey: string): Promise<boolean> {
        return new Promise((resolve) => {
            const alert = this.alertCtrl.alert({
                header: this.lang.tr(headerKey),
                message: this.lang.tr(messageKey),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
                        return false;
                    },
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    },
                }],
            });

            alert.onDidDismiss().then((detail: { data?: boolean }) => {
                resolve(detail.data === true);
            });

            void alert.present();
        });
    }

    /** Info alert when send is tapped while QR/hours block orders. */
    private _ShowOrdersBlocked(): Promise<void> {
        return new Promise((resolve) => {
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@place_all_closed_title'),
                message: this.lang.tr('@place_all_closed_message'),
                backdropDismiss: true,
                buttons: [{
                    text: this.lang.tr('@ok'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
                        return false;
                    },
                }],
            });

            alert.onDidDismiss().then(() => {
                resolve();
            });

            void alert.present();
        });
    }
}
